import React, { useState, useEffect } from 'react';
import AdminLayoutWithSubscription from '@/components/admin/AdminLayoutWithSubscription';
import { useAdminSettings } from '@/hooks/useAdminSettings';
import { Card } from '@/components/ui/card';
import KnowledgeBaseHeader from '@/components/knowledge-base/KnowledgeBaseHeader';
import FeatureGuidesSidebar from '@/components/knowledge-base/FeatureGuidesSidebar';
import GuideDetail from '@/components/knowledge-base/GuideDetail';
import { FeatureGuide } from '@/types/knowledgeBase';
import { getAdminFeatureGuides } from '@/data/adminFeatureGuides';
import { hasAccess } from '@/utils/permissionService';
import { useNavigate } from 'react-router-dom';

const AdminKnowledgeBasePage: React.FC = () => {
  const navigate = useNavigate();
  const { isLoading, user, handleLogout } = useAdminSettings();
  const [featureGuides, setFeatureGuides] = useState<FeatureGuide[]>([]);
  const [filteredGuides, setFilteredGuides] = useState<FeatureGuide[]>([]);
  const [selectedGuide, setSelectedGuide] = useState<FeatureGuide | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (user) {
      const guides = getAdminFeatureGuides(user.role);
      setFeatureGuides(guides);
      setFilteredGuides(guides);
      if (guides.length > 0) {
        setSelectedGuide(guides[0]);
      }
    }
  }, [user]);

  useEffect(() => {
    if (featureGuides.length > 0) {
      let filtered = [...featureGuides];
      
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(
          guide => 
            guide.title.toLowerCase().includes(term) || 
            guide.description.toLowerCase().includes(term) ||
            guide.content.toLowerCase().includes(term)
        );
      }
      
      if (activeTab !== 'all') {
        filtered = filtered.filter(guide => guide.category.toLowerCase() === activeTab.toLowerCase());
      }
      
      setFilteredGuides(filtered);
      
      if (filtered.length > 0 && selectedGuide && !filtered.some(guide => guide.id === selectedGuide.id)) {
        setSelectedGuide(filtered[0]);
      }
    }
  }, [searchTerm, activeTab, featureGuides, selectedGuide]);
  
  useEffect(() => {
    if (!isLoading && user) {
      const canAccess = user.isAdmin || hasAccess('admin-knowledge-base');
      if (!canAccess) {
        navigate('/admin/dashboard');
      }
    }
  }, [isLoading, user, navigate]);
  
  const categories = ['all', ...new Set(featureGuides.map(guide => guide.category.toLowerCase()))];

  if (isLoading) {
    return (
      <AdminLayoutWithSubscription
        isLoading={true}
        onLogout={handleLogout}
      >
        <div>Loading...</div>
      </AdminLayoutWithSubscription>
    );
  }

  return (
    <AdminLayoutWithSubscription
      isLoading={false}
      onLogout={handleLogout}
      userRole={user?.role}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <KnowledgeBaseHeader 
            searchTerm={searchTerm} 
            setSearchTerm={setSearchTerm}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FeatureGuidesSidebar
            filteredGuides={filteredGuides}
            selectedGuide={selectedGuide}
            setSelectedGuide={setSelectedGuide}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            categories={categories}
          />
          <div className="md:col-span-2">
            <GuideDetail 
              selectedGuide={selectedGuide}
              allGuides={featureGuides}
              onSelectGuide={setSelectedGuide}
            />
          </div>
        </div>
      </div>
    </AdminLayoutWithSubscription>
  );
};

export default AdminKnowledgeBasePage;
